<template>
  <div>
    <div class="container">

      <Panel :title="$t('settings.title')">
        <div class="columns">
          <div class="column">
            <!-- Preset -->
            <div class="group">
              <label>{{$t('preset')}}</label>
              <select v-model="selected.preset">
                <option :value="null"></option>
                <option v-for="preset of allowedPresets" :value="preset" :key="`preset_${preset.name}`">{{$t(`statistics.preset.${preset.name}`)}}</option>
              </select>
            </div>
  
            <!-- Report -->
            <div class="group">
              <label>{{$t('report_type')}}</label>
              <select v-model="selected.report">
                <option v-for="report of allowedReports" :value="report.name" :key="`report_${report.name}`">{{ $t(`statistics.type.${report.name}`)}}</option>
              </select>
            </div>
  
            <!-- Filters -->
            <div class="group" v-if="selected.report">
              <label>{{$t('filters')}}</label>
              <div class="filter-line" v-for="(selectedFilter, index) of selected.filters" :key="`filter_${index}`">
                <!-- Filter type -->
                <select v-model="selectedFilter.name" @change="onFilterTypeChange(selectedFilter, index)">
                  <option :value="null"/>
                  <option :value="filter.name" v-for="filter of allowedFilters">{{ $t(`statistics.param.${filter.name}`) }}</option>
                </select>
  
                <!-- Selects -->
                <datetime
                    v-if="isDatetimeFilter(selectedFilter.name)"
                    v-model="selectedFilter.value"
                    type="datetime"
                    value-zone="local"
                    :input-id="`date_${index}`"
                    input-style="width: 100%"
                />
                <select v-else-if="isBoolFilter(selectedFilter.name)" v-model="selectedFilter.value" >
                  <option :value="true">{{$t('yes')}}</option>
                  <option :value="false">{{$t('no')}}</option>
                </select>
                <AjaxSelect
                    v-else-if="isEntityFilter(selectedFilter.name)"
                    v-model="selectedFilter.value"
                    :api="`/api/entity/${entityName(selectedFilter.name)}/select`"
                    :map-fn="(i) => i.name"
                    :allow-empty-search="true"
                />
                <input v-else-if="isInputFilter(selectedFilter.name)" type="text" :value="selectedFilter.value">
                <select v-else-if="selectedFilter.name === 'deliveryMethod'" v-model="selectedFilter.value">
                  <option :value="null"></option>
                  <option value="delivery">{{$t('deliveryMethod.delivery')}}</option>
                  <option value="takeout">{{$t('deliveryMethod.takeout')}}</option>
                  <option value="inPlace">{{$t('deliveryMethod.inPlace')}}</option>
                  <option value="hotel">{{$t('deliveryMethod.hotel')}}</option>
                  <option value="catering">{{$t('deliveryMethod.catering')}}</option>
                </select>
                <div v-else><!-- Not mapped or empty --></div>
              </div>
            </div>
  
            <!-- GroupBy -->
            <div class="group" v-if="selected.report">
              <label>{{$t('groupBy')}}</label>
              <div class="group-by-line" v-for="(selectedGroupBy, index) of selected.groupBy" :key="`gb_${index}`">
                <select v-model="selectedGroupBy.name" @change="onGroupByChange(selectedGroupBy, index)">
                  <option :value="null"/>
                  <option :value="groupBy.name" v-for="groupBy of activeGroupBy">{{ $t(`statistics.param.${groupBy.name}`) }}</option>
                </select>
              </div>
            </div>
            <!-- Servers -->
            <div class="group" v-if="selected.report && accessibleServers">
              <label>{{$t('servers')}}</label>
              <MultiSelect
                :options="accessibleServers"
                :selected="selected.servers"
                :hideSelected="true"
                trackByAttribute="id"
                @changed="selected.servers = $event"
              />
            </div>
            <!-- Accessible branches -->
            <div class="group" v-if="selected.report && accessibleBranches">
              <label>{{$t('branches.title')}}</label>
              <MultiSelect
                :options="accessibleBranches"
                :selected="selected.branches"
                :hideSelected="true"
                trackByAttribute="id"
                @changed="selected.branches = $event"
              />
            </div>
  
          </div>
  
          <div class="column">
            <!-- Columns -->
            <label style="margin-bottom: 5px">{{$t('columns')}}</label>
            <div class="group" v-for="field of activeFields" :key="`field_${field.name}`">
              <input :id="`chk_field_${field.name}`" type="checkbox" :value="field.name" v-model="selected.fields">
              <label :for="`chk_field_${field.name}`">{{$t(`statistics.column.${field.name}`)}}</label>
            </div>
          </div>
  
        </div>
  
        <div style="margin-top: 10px;">
          <button class="btn btn-primary" @click="generate">{{$t('generate')}}</button>
        </div>

      </Panel>

      <Panel ref="dataTab" title="Statistiques" v-if="builtData">
        <RawTable v-if="builtData" :columns="builtData.columns" :rows="builtData.data" :key-value-header="builtData.options.filters"/>
      </Panel>

    </div>
  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import moment from "moment-timezone";
  import AjaxSelect from "@/components/tools/AjaxSelect.vue";
  import MultiSelect from '@/components/tools/MultiSelect.vue';
  import RawTable from '@/components/RawTable.vue';

  export default {
    name: "StatsBuilder",

    components: { AjaxSelect, MultiSelect, RawTable },

    data() {
      return {
        presets: [
          {
            name: 'globalSaleStats',
            report: 'sales',
            filters: {
              start: moment().startOf('day').subtract(1, 'year').toISOString(),
              end: moment().endOf('day').toISOString()
            },
            groupBy: {},
            fields: ['nbUniqueCustomer', 'nbSale', 'total']
          },
          {
            name: 'globalPaymentStats',
            report: 'payments',
            filters: {
              start: moment().startOf('day').subtract(1, 'year').toISOString(),
              end: moment().endOf('day').toISOString(),
              online: true
            },
            groupBy: {
              processor: true
            },
            fields: ['nbPayment', 'total']
          }
        ],
        reports: [
          {
            name: 'sales',
            permission: 'atLeastSuperAdmin',
            filters: [
              { name: 'start',   type: 'datetime' },
              { name: 'end',     type: 'datetime' },
              { name: 'branch',  type: 'entity|Branch', permission: 'atLeastSuperAdmin' },
              { name: 'company', type: 'entity|Company', permission: 'atLeastSuperAdmin' },
              { name: 'appName', type: 'input' },
              { name: 'deliveryMethod' },
              { name: 'paymentOnly', type: 'bool' },
            ],
            groupBy: [
              { name: 'day' },
              { name: 'hour' },
              { name: 'week' },
              { name: 'week_day' },
              { name: 'week_day_hour' },
              { name: 'month' },
              { name: 'year' },
              //{ name: 'item' },
              //{ name: 'category' },
              { name: 'appName' },
              { name: 'deliveryMethod' },
              { name: 'affiliation' },
            ],
            fields: [
              { name: 'nbSale' },
              { name: 'nbUniqueCustomer' },
              { name: 'subTotal' },
              { name: 'total' },
              { name: 'tipTotal' },
              { name: 'deliveryFees' },
              { name: 'avgTotal' },
            ]
          },
          {
            name: 'payments',
            permission: 'atLeastBranchAdmin',
            filters: [
              { name: 'start',   type: 'datetime' },
              { name: 'end',     type: 'datetime' },
              { name: 'branch',  type: 'entity|Branch', permission: 'atLeastSuperAdmin' },
              { name: 'company', type: 'entity|Company', permission: 'atLeastSuperAdmin' },
              { name: 'appName', type: 'input' },
              { name: 'online',  type: 'bool' },
              { name: 'deliveryMethod' },
            ],
            fields: [
              { name: 'nbPayment' },
              { name: 'total' },
            ],
            groupBy: [
              { name: 'processor' }
            ]
          }
        ],
        selected: {
          preset: null,
          report: null,
          filters: [],
          groupBy: [],
          fields: [],
          servers: [],

          branches: []
        },
        builtData: undefined
      }
    },

    computed: {
      allowedPresets() {
        return this.presets.filter(p => this.hasAccessToReport(this.findReportByName(p.report)));
      },
      allowedReports() {
        return this.reports.filter(this.hasAccessToReport);
      },
      allowedFilters() {
        return this.activeFilters.filter(this.hasAccessToFilter);
      },
      activeFilters() {
        return this.selectedReportDefinition ? this.selectedReportDefinition.filters : null;
      },
      activeGroupBy() {
        return this.selectedReportDefinition ? this.selectedReportDefinition.groupBy : null;
      },
      activeFields() {
        return this.selectedReportDefinition ? this.selectedReportDefinition.fields : null;
      },
      selectedReportDefinition() {
        return this.selected.report ? this.findReportByName(this.selected.report) : null;
      },
      accessibleServers() {
        return this.$store.state.loggedUser.accessibleServers
          ? this.$store.state.loggedUser.accessibleServers.map(({id, name}) => ({id: id, label: name}))
          : null;
      },
      accessibleBranches() {
        if (this.exactlyDistributor) {
          return (this.$store.state.loggedUser.accessibleBranches || [])
            .filter(sb => sb.server.id === this.contextServer.id)
            .map(sb => ({id: sb.branchId, label: sb.name}));
        } else {
          return undefined;
        }
      },
      mainServer() {
        return this.$store.state.loggedUser.accessibleServers
          ? (this.$store.state.loggedUser.accessibleServers.find(s => !s.remote) || null)
          : null;
      },
      contextServer() {
        return this.$store.state.contextServer;
      },
      atLeastSuperAdmin() {
        return this.$store.getters.loggedUserHasSuperAdmin;
      },
      atLeastDistributor() {
        return this.$store.getters.loggedUserHasDistributor;
      },
      atLeastBranchAdmin() {
        return this.$store.getters.loggedUserHasBranchAdmin;
      },
      exactlyDistributor() {
        return this.$store.state.loggedUser.isDistributor;
      },
      exactlyBranchAdmin() {
        return this.$store.state.loggedUser.isBranchAdmin;
      },
    },

    watch: {
      'selected.report'(reportName) {
        if (this.selected.preset && this.selected.preset.report !== reportName) {
          this.selected.preset = null;
        }
      },
      'selected.preset'(preset) {
        if (preset) {
          this.selected.report = preset.report;
          this.selected.filters = preset.filters ? Object.entries(preset.filters).map(([name,value]) => ({name, value})): [];
          this.selected.groupBy = preset.groupBy ? Object.entries(preset.groupBy).map(([name,value]) => ({name, value})) : [];
          this.selected.fields = preset.fields ? JSON.parse(JSON.stringify(preset.fields)) : [];
        }
      },
      'selected.filters': {
        immediate: true,
        deep: true,
        handler(filters) {
          if (!filters.find(f => f.name === null)) {
            filters.push({name: null, value: null})
          }
        }
      },
      'selected.groupBy': {
        immediate: true,
        deep: true,
        handler(groupBy) {
          if (!groupBy.find(f => f.name === null)) {
            groupBy.push({name: null})
          }
        }
      },
      accessibleServers: {
        immediate: true,
        handler(servers) {
          this.selected.servers = servers || [];
        }
      },
      accessibleBranches: {
        immediate: true,
        handler(branches) {
          this.selected.branches = branches || [];
        }
      }
    },

    mounted() {
      this.$store.state.pageTitleTranslationKey = "statistics.title";
      this.$store.state.pageIcon = "/icon/nav/analytics.svg";

      this.selected.preset = this.allowedPresets[0] || null;
    },

    methods: {
      findReportByName(reportName) {
        return this.reports.find(r => r.name === reportName) || null;
      },
      getFilterType(filterName) {
        let type = null;
        if (this.activeFilters) {
          let filter = this.activeFilters.find(f => f.name === filterName);
          if (filter) {
            type = filter.type;
          }
        }
        return type || '';
      },
      entityName(filterName) {
        return (this.getFilterType(filterName).split('|')[1] || '').toLowerCase();
      },
      isEntityFilter(filterName) {
        return this.getFilterType(filterName).includes('entity')
      },
      isDatetimeFilter(filterName) {
        return this.getFilterType(filterName) === 'datetime'
      },
      isBoolFilter(filterName) {
        return this.getFilterType(filterName) === 'bool'
      },
      isInputFilter(filterName) {
        return this.getFilterType(filterName) === 'input'
      },
      onFilterTypeChange(filter, index) {
        if (filter.name === null) {
          this.selected.filters.splice(index, 1);
        } else {
          filter.value = this.isEntityFilter(filter.name) ? {id: null} : null;
        }
      },
      onGroupByChange(groupBy, index) {
        if (groupBy.name === null) {
          this.selected.groupBy.splice(index, 1);
        }
      },
      fixDateTime(dateString) {
        return dateString.substring(0, 16).replace('T', ' ');
      },
      hasAccessToReport(report) {
        return report && this.hasAccess(report.permission);
      },
      hasAccessToFilter(filter) {
        return filter && (!filter.permission || this.hasAccess(filter.permission));
      },
      hasAccess(permission) {
        return (!permission && this.atLeastSuperAdmin)
          || (permission === 'atLeastSuperAdmin' && this.atLeastSuperAdmin)
          || (permission === 'atLeastDistributor' && this.atLeastDistributor)
          || (permission === 'atLeastBranchAdmin' && this.atLeastBranchAdmin);
      },

      async generate() {
        let filterReducer = (obj, filter) => {
          if (!obj) { obj = {} }
          obj[filter.name] = filter.value;
          return obj
        };

        let data = {
          filters: this.selected.filters.filter(e => !!e.name).reduce(filterReducer, undefined),
          groupBy: this.selected.groupBy.filter(e => !!e.name).map(g => g.name),
          fields: this.selected.fields.length ? this.selected.fields : undefined,
          servers: this.selected.servers.length ? this.selected.servers.map(s => s.id) : undefined
        };
        /* Force update filters.branches */
        if (this.accessibleBranches !== undefined) {
          data.filters.branches = this.selected.branches.map(b => b.id);
        }
        /* Force update filters.branch */
        if (this.exactlyBranchAdmin) {
          data.filters.branch = {id: this.$store.state.loggedUser.branch};
        }

        for (let [key, value] of Object.entries(data.filters || {})) {
          if (this.isDatetimeFilter(key)) {
            data.filters[key] = this.fixDateTime(value);
          }
          else if (this.isEntityFilter(key)) {
            data.filters[key] = value.id ? value.id : value;
          }
        }
        /* Checks */
        let errors = [];
        if (data.filters && data.filters.company && data.servers && data.servers.length > 1) {
          errors.push("Cannot filter by company with multiple servers");
        }
        if (data.filters && data.filters.branch && data.servers && data.servers.length > 1) {
          errors.push("Cannot filter by branch with multiple servers");
        }
        if (errors.length > 0) {
          toast(errors.join('<br/>'), 'error');
          return;
        }

        /* Always call main server when multiple servers selected */
        let options = {};
        if (data.servers && this.mainServer) {
          options.baseURL = this.mainServer.url;
        }

        try {
          this.$store.commit("showSpinner", true);
          let response = await Core.request("POST", `/api/stats-builder/${this.selected.report}`, data, options);
          this.$store.commit("showSpinner", false);
          if (response) {
            this.builtData = response;
            setTimeout(() => {
              this.$refs.tabs.setActive(this.$refs.dataTab);
            }, 0);
          }
        } catch (e) {
          this.$store.commit("showSpinner", false);
          console.log("error", e);
          toast(this.$t("error.no_server"), 'error');
        }
      },

      print() {
        if (!this.builtData) {
          toast('Please generate a report first', 'warning')
        } else {
          this.$htmlToPaper('printTable');
        }
      },

      downloadAsCSV() {
        window._download(
            `stats_${Date.now()}.csv`,
            'data:text/csv;charset=utf-8,'
            + this.builtData.columns.map(r => r.name).join(',') + '\n'
            + this.builtData.data.map(r => r.join(',')).join('\n')
        );
      }
    }
  }
</script>

<style lang="scss" scoped>
.filter-line {
  display: flex;
  > * {
    flex-basis: 50%;
  }
}
.group-by-line {
  > * {
    width: 100%;
  }
}
</style>